import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Divider,
} from "@mui/material";
import { formatDate } from "../utils/date";
import api from "../Services/http";

export default function EmailDetailModal({ open, onClose, jobId, jobName, reportData }) {
    const [loading, setLoading] = useState(false);
    const [emailData, setEmailData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Si les données du rapport sont passées directement, on les utilise sans requête
        if (open && reportData) {
            setEmailData(reportData);
            setError(null);
            setLoading(false);
            return;
        }

        const loadEmailDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                // Récupérer le dernier statut du job qui contient le dernier email
                const response = await api.get(
                    `/api/email-report-statuses?jobId=${jobId}&limit=1`,
                );

                const result = response.data;
                const statuses = result.data || [];

                if (statuses.length > 0 && statuses[0].lastEmail) {
                    setEmailData(statuses[0]);
                } else {
                    setError("Aucun email trouvé pour cette sauvegarde");
                }
            } catch (err) {
                console.error("Error loading email details:", err);
                setError("Impossible de charger les détails de l'email");
            } finally {
                setLoading(false);
            }
        };

        if (open && jobId) {
            loadEmailDetails();
        } else {
            setEmailData(null);
            setError(null);
        }
    }, [open, jobId, reportData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Dernier email reçu - {jobName}</DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Typography color="error" align="center">
                        {error}
                    </Typography>
                )}

                {!loading && !error && emailData && emailData.lastEmail && (
                    <Box>
                        {/* Statut */}
                        <Box mb={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                État de la sauvegarde
                            </Typography>
                            <Chip
                                label={emailData.statusType?.name || "UNKNOWN"}
                                color={
                                    emailData.statusType?.name === "OK"
                                        ? "success"
                                        : emailData.statusType?.name ===
                                            "FAILED"
                                          ? "error"
                                          : emailData.statusType?.name ===
                                              "WARNING"
                                            ? "warning"
                                            : "default"
                                }
                                size="small"
                                sx={{ mt: 0.5 }}
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Infos email */}
                        <Box mb={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                De
                            </Typography>
                            <Typography variant="body1">
                                {emailData.lastEmail.from}
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                À
                            </Typography>
                            <Typography variant="body1">
                                {emailData.lastEmail.to}
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Date de réception
                            </Typography>
                            <Typography variant="body1">
                                {formatDate(emailData.lastEmail.dateReceived)}
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Sujet
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {emailData.lastEmail.subject}
                            </Typography>
                        </Box>

                        {emailData.lastEmail.body && (
                            <Box mb={2}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Corps du message
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 2,
                                        bgcolor: "grey.100",
                                        borderRadius: 1,
                                        maxHeight: 300,
                                        overflow: "auto",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            fontFamily: "monospace",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {emailData.lastEmail.body}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {emailData.summary && (
                            <Box mb={2}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Résumé
                                </Typography>
                                <Typography variant="body2">
                                    {emailData.summary}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Dernière vérification
                            </Typography>
                            <Typography variant="body2">
                                {formatDate(emailData.checkedAt)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {!loading && !error && !emailData && (
                    <Typography color="text.secondary" align="center">
                        Aucun email trouvé pour cette sauvegarde
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
}
